<template>
  <div class="page-header" :class="{ 'page-header--sticky': sticky }">
    <div class="page-header__container">
      <!-- Left: Title Section -->
      <div class="page-header__title-section">
        <v-icon v-if="icon" :color="iconColor" :size="iconSize" class="page-header__icon">
          {{ icon }}
        </v-icon>
        <div class="page-header__title-wrapper">
          <h1 class="page-header__title">{{ title }}</h1>
          <div v-if="breadcrumbs.length > 0 || subtitle" class="page-header__breadcrumbs">
            <template v-if="breadcrumbs.length > 0">
              <span
                v-for="(crumb, index) in breadcrumbs"
                :key="index"
                class="breadcrumb-item"
              >
                <router-link v-if="crumb.to" :to="crumb.to" class="breadcrumb-link">
                  {{ crumb.text }}
                </router-link>
                <span v-else class="breadcrumb-text">{{ crumb.text }}</span>
                <v-icon
                  v-if="index < breadcrumbs.length - 1"
                  small
                  class="breadcrumb-separator"
                >
                  mdi-chevron-right
                </v-icon>
              </span>
            </template>
            <template v-else>
              <span class="page-header__subtitle">{{ subtitle }}</span>
            </template>
          </div>
        </div>
      </div>

      <!-- Right: Actions Section -->
      <div v-if="$slots.actions || $slots.default" class="page-header__actions">
        <slot name="actions">
          <slot />
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PageHeader",
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    iconColor: {
      type: String,
      default: "primary",
    },
    iconSize: {
      type: [String, Number],
      default: 32,
    },
    breadcrumbs: {
      type: Array,
      default: () => [],
    },
    sticky: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
.page-header {
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  transition: box-shadow 0.2s ease;
}

.page-header--sticky {
  position: sticky;
  top: 64px;
  z-index: 1;
}

.page-header--sticky.scrolled {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.page-header__container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px;
  min-height: 72px;
  max-width: 100%;
}

@media (min-width: 600px) {
  .page-header__container {
    padding: 18px 16px;
    min-height: 80px;
  }
}

@media (min-width: 960px) {
  .page-header__container {
    padding: 20px 24px;
    min-height: 88px;
  }
}

.page-header__title-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

@media (min-width: 600px) {
  .page-header__title-section {
    gap: 10px;
  }
}

@media (min-width: 960px) {
  .page-header__title-section {
    gap: 12px;
  }
}

.page-header__icon {
  flex-shrink: 0;
}

.page-header__title-wrapper {
  flex: 1;
  min-width: 0;
}

.page-header__title {
  font-size: 18px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (min-width: 600px) {
  .page-header__title {
    font-size: 20px;
  }
}

@media (min-width: 960px) {
  .page-header__title {
    font-size: 24px;
  }
}

.page-header__breadcrumbs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

.page-header__subtitle {
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
}

.breadcrumb-link {
  color: #2196f3;
  text-decoration: none;
  transition: color 0.2s ease;
}

.breadcrumb-link:hover {
  color: #1976d2;
  text-decoration: underline;
}

.breadcrumb-text {
  color: rgba(0, 0, 0, 0.6);
}

.breadcrumb-separator {
  margin: 0 4px;
  color: rgba(0, 0, 0, 0.38);
}

.page-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 8px;
}

@media (min-width: 600px) {
  .page-header__actions {
    gap: 10px;
    margin-left: 12px;
  }
}

@media (min-width: 960px) {
  .page-header__actions {
    gap: 12px;
    margin-left: 24px;
  }
}

/* Responsive - Small screens */
@media (max-width: 959px) {
  .page-header__container {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .page-header__actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
  }
}

@media (max-width: 599px) {
  .page-header__breadcrumbs {
    font-size: 12px;
  }

  .page-header__actions {
    flex-wrap: wrap;
    gap: 6px;
  }

  .page-header__icon {
    display: none;
  }
}
</style>
